import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, ShoppingCart, ChevronDown, Home, LayoutGrid, Store as StoreIcon, Package, User, MessageSquare } from "lucide-react";
import { useLocation } from "@/context/LocationContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import LocationModal from "@/components/LocationModal";
import CartDrawer from "@/components/CartDrawer";
import PWAPrompt from "@/components/PWAPrompt";
import InstallApp from "@/components/InstallApp";
import { ReferralBanner } from "@/components/ReferralBanner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";

export default function CustomerLayout({ children }) {
  const nav = useNavigate();
  const { location } = useLocation();
  const { count, pending, confirmReplace, cancelReplace } = useCart();
  const { user } = useAuth();
  const [locOpen, setLocOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [q, setQ] = useState("");

  const submitSearch = (e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q)}`); };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <ReferralBanner />
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" data-testid="nav-brand-logo" className="flex shrink-0 items-center">
            <img src="/logo.png" alt="Grofkart" className="h-8 w-auto sm:h-9" />
          </Link>

          <button data-testid="nav-location-selector" onClick={() => setLocOpen(true)}
            className="flex max-w-[42%] shrink-0 items-center gap-1 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-slate-100">
            <MapPin className="h-4 w-4 shrink-0 text-brand" />
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase text-slate-400">Delivering to</p>
              <p className="flex items-center gap-1 truncate text-xs font-semibold text-slate-800">
                {location.city || location.address} <ChevronDown className="h-3 w-3 shrink-0" />
              </p>
            </div>
          </button>

          <form onSubmit={submitSearch} className="relative hidden flex-1 md:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search for products, brands and stores"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-4 text-sm outline-none focus:border-brand focus:bg-white" />
          </form>

          <button data-testid="nav-cart-btn" onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 rounded-xl bg-brand px-3 py-2 text-sm font-semibold text-white transition-transform hover:bg-brand-deep active:scale-95">
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:block">Cart</span>
            {count > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-surge px-1 text-[10px] font-bold text-white">{count}</span>}
          </button>

          <button data-testid="nav-community-btn" onClick={() => nav("/community")}
            className="hidden items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100 lg:flex">
            <MessageSquare className="h-4 w-4" /> Community
          </button>
          <button data-testid="nav-shyama-btn" onClick={() => nav("/shyama-foundation")}
            className="hidden items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-800 transition-colors hover:bg-emerald-100 lg:flex">
            Shyama Foundation
          </button>
          <button data-testid="nav-become-partner-btn" onClick={() => nav("/partner")}
            className="hidden items-center gap-1.5 rounded-xl bg-amber-soft px-3 py-2 text-sm font-bold text-amber-warm transition-colors hover:bg-amber-border lg:flex">
            <StoreIcon className="h-4 w-4" /> Become a Partner
          </button>
          <InstallApp variant="header" />
          <button data-testid="nav-account-btn" onClick={() => nav(user ? "/account" : "/login")}
            className="hidden items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 md:flex">
            <User className="h-4 w-4" /> {user ? user.name.split(" ")[0] : "Login"}
          </button>
        </div>

        {/* Mobile search */}
        <form onSubmit={submitSearch} className="relative px-4 pb-3 md:hidden">
          <Search className="absolute left-7 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products & stores"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-4 text-sm outline-none focus:border-brand focus:bg-white" />
        </form>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-50 flex h-16 items-center justify-around border-t border-slate-200 bg-white/95 backdrop-blur-xl md:hidden">
        <BottomLink testid="mobile-nav-home" to="/" icon={Home} label="Home" />
        <BottomLink testid="mobile-nav-categories" to="/categories" icon={LayoutGrid} label="Categories" />
        <BottomLink testid="mobile-nav-stores" to="/stores" icon={StoreIcon} label="Shops" />
        <BottomLink testid="mobile-nav-community" to="/community" icon={MessageSquare} label="Community" />
        <BottomLink testid="mobile-nav-orders" to="/orders" icon={Package} label="Orders" />
        <BottomLink testid="mobile-nav-account" to={user ? "/account" : "/login"} icon={User} label="Account" />
      </nav>

      <LocationModal open={locOpen} onClose={() => setLocOpen(false)} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <PWAPrompt />

      {/* Floating support chat launcher */}
      <button
        data-testid="chat-support-fab"
        onClick={() => nav(user ? "/support" : "/login")}
        aria-label="Chat with support"
        className="fixed bottom-24 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-lg shadow-brand/30 transition-transform hover:scale-105 active:scale-95 md:bottom-6 md:right-6"
      >
        <MessageSquare className="h-6 w-6" />
        <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-amber-surge ring-2 ring-white" />
      </button>

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && cancelReplace()}>
        <AlertDialogContent data-testid="replace-cart-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start a new cart?</AlertDialogTitle>
            <AlertDialogDescription>
              Your cart has items from another store. Replace them with items from {pending?.store?.name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelReplace}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="replace-cart-confirm" onClick={confirmReplace} className="bg-brand hover:bg-brand-deep">Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BottomLink({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex flex-col items-center gap-0.5 text-slate-500 transition-colors hover:text-brand">
      <Icon className="h-5 w-5" /><span className="text-[10px] font-medium">{label}</span>
    </Link>
  );
}
